import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Divider, Paper, Rating, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAddWorkshopMutation } from "../../../../app/store/api/workshopApi";
import toast from "react-hot-toast";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { googleMapsLibraries, googleMapsApiKey } from "../../../../shared/configs/googleMaps";
import { defaultLocation } from "../../../../app/store/workshopSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const MAP_STYLE = { width: "100%", height: 600, borderRadius: 8 };

// Advanced markers require a Map ID. "DEMO_MAP_ID" works for development;
// for production create your own in Google Cloud Console (Map Management) and put it here.
const MAP_ID = "DEMO_MAP_ID";
const MAP_OPTIONS = { mapId: MAP_ID, streetViewControl: false, mapTypeControl: false };

const PLACE_FIELDS = ["id", "displayName", "formattedAddress", "location", "rating", "userRatingCount", "reviews"];

const INITIAL_FORM = {
  name: "",
  location: null,
  address: "",
  placeId: null,
  ratings: { rating: 0, userRatingCount: 0 },
};

const toPayload = (form) => form;

const labelSx = { fontWeight: 600, color: "text.primary", lineHeight: 1.3 };

const fromNewPlace = (p) => ({
  id: p.id || null,
  rating: p.rating ?? 0,
  userRatingCount: p.userRatingCount ?? 0,
  reviews: (p.reviews || []).map((r) => ({
    author: r.authorAttribution?.displayName,
    rating: r.rating,
    time: r.relativePublishTimeDescription,
    text: r.text,
  })),
});

const PlaceSearchField = ({ value, onValueChange, onPlaceSelected, onError, placeholder }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const input = query.trim();
    const requestId = ++requestIdRef.current;

    if (input.length < 2) {
      setOptions([]);
      setSearching(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary("places");
        if (!sessionTokenRef.current) sessionTokenRef.current = new AutocompleteSessionToken();

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: ["fi"],
          sessionToken: sessionTokenRef.current,
        });

        if (requestId === requestIdRef.current) {
          setOptions(suggestions.filter((s) => s.placePrediction));
        }
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = async (_, option) => {
    if (!option || typeof option === "string") return;
    const place = option.placePrediction.toPlace();
    sessionTokenRef.current = null;
    setQuery("");
    setOptions([]);
    setSearching(true);
    try {
      await onPlaceSelected(place);
    } catch {
      onError?.();
    } finally {
      setSearching(false);
    }
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      blurOnSelect
      value={null}
      options={options}
      filterOptions={(x) => x}
      inputValue={value}
      onInputChange={(_, v, reason) => {
        if (reason === "reset") return;
        onValueChange(v);
        if (reason === "input") setQuery(v);
      }}
      onChange={handleSelect}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.placePrediction.text.toString())}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const p = option.placePrediction;
        return (
          <li key={key} {...rest}>
            <Box>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {p.mainText?.text ?? p.text.toString()}
              </Typography>
              {p.secondaryText?.text && (
                <Typography variant='caption' color='text.secondary'>
                  {p.secondaryText.text}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searching && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const AddWorkshop = ({ onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_FORM);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries: googleMapsLibraries,
  });
  const storedDefaultLocation = useSelector(defaultLocation);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const reverseGeocodeRef = useRef(null);
  const initialCenter = useRef(storedDefaultLocation).current;

  const [map, setMap] = useState(null);
  const [position, setPosition] = useState(storedDefaultLocation);
  const positionRef = useRef(position);
  positionRef.current = position;
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addWorkshop] = useAddWorkshopMutation();
  const disabled = !name || !address || !details;
  const addDataToForm = useCallback(({ name, lat, lng, addr, place }) => {
    const loc = { lat, lng };
    const ratings = { rating: place?.rating ?? 0, userRatingCount: place?.userRatingCount ?? 0 };
    setPosition(loc);
    setName(name);
    setAddress(addr);
    setDetails(place ? { name, ratings, reviews: place.reviews } : null);
    setError("");
    setForm({ name, location: loc, address: addr, placeId: place?.id || null, ratings });
  }, []);

  const moveMap = (loc, zoom = 17) => {
    mapRef.current?.panTo(loc);
    mapRef.current?.setZoom(zoom);
  };

  const applyPlace = async (place, { pan = true } = {}) => {
    await place.fetchFields({ fields: PLACE_FIELDS });
    if (!place.location) throw new Error("Place has no location");

    const lat = place.location.lat();
    const lng = place.location.lng();
    addDataToForm({
      name: place.displayName || "",
      lat,
      lng,
      addr: place.formattedAddress || "",
      place: fromNewPlace(place),
    });
    if (pan) moveMap({ lat, lng });
  };

  const loadPlaceDetails = async (placeId, latLng) => {
    setLoading(true);
    let ok = false;
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      await applyPlace(new Place({ id: placeId }), { pan: false });
      ok = true;
    } catch {
      // fall through to reverse geocoding
    }
    setLoading(false);
    if (!ok) reverseGeocode(latLng);
  };

  const reverseGeocode = (latLng) => {
    const lat = latLng.lat();
    const lng = latLng.lng();
    setPosition({ lat, lng });
    setLoading(true);
    new window.google.maps.Geocoder().geocode({ location: latLng }, (results, status) => {
      setLoading(false);
      if (status === "OK" && results?.[0]) {
        addDataToForm({ name: "", lat, lng, addr: results[0].formatted_address });
      } else {
        addDataToForm({ name: "", lat, lng, addr: "" });
        setError(t("addWorkshop.geocodeFailed", "Tätä paikkaa ei voitu ladata. Kokeile toista ehdotusta."));
      }
    });
  };

  reverseGeocodeRef.current = reverseGeocode;

  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
      if (cancelled) return;
      const marker = new AdvancedMarkerElement({
        map,
        position: positionRef.current,
        gmpDraggable: true,
      });
      marker.addEventListener("gmp-dragend", (e) => {
        if (e.latLng) reverseGeocodeRef.current?.(e.latLng);
      });
      markerRef.current = marker;
    })();

    return () => {
      cancelled = true;
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (markerRef.current) markerRef.current.position = position;
  }, [position]);

  const handleMapClick = (e) => {
    if (!e.latLng) return;
    setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    if (e.placeId) {
      e.stop();
      loadPlaceDetails(e.placeId, e.latLng);
    } else {
      reverseGeocode(e.latLng);
    }
  };

  const handlePlaceError = () => setError(t("addWorkshop.placeFailed", "Osoitetta ei voitu lukea tälle pisteelle."));

  const handleSave = async () => {
    const payload = toPayload(form);
    const result = await addWorkshop(payload);
    if (result?.error) {
      if(result?.error?.status === 422){
        toast.error(t("addWorkshop.workshopExists", "Workshop already exists."));
      }
      else{
        toast.error(t("addWorkshop.genericError", "An error occurred while adding the workshop."));
      }
      handleReset();
    } else {
      toast.success(t("addWorkshop.successMessage", "Workshop added successfully."));
    }
  };

  const handleReset = () => {
    setName("");
    setAddress("");
    setForm(INITIAL_FORM);
    setPosition(storedDefaultLocation);
    setDetails(null);
    setError("");
    if (storedDefaultLocation) moveMap(storedDefaultLocation, 14);
  };

  if (loadError) return <Alert severity='error'>Google Maps failed to load.</Alert>;
  if (!isLoaded) return <CircularProgress />;

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <Button
          // variant="contained"
          color='black'
          onClick={onClose}
          startIcon={<ArrowBackIcon />}
        >
          {t("addWorkshop.back")}
        </Button>
      </Box>
      <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
            {t("workshopInfo.workshopInformation")}
          </Typography>
          <Button variant='contained' onClick={handleReset} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" } }}>
            {t("addWorkshop.reset", "Tyhjennä")}
          </Button>
        </Box>

        <Stack spacing={1}>
          <Typography variant='body2' sx={labelSx}>
            {t("addWorkshop.workshopName", "Korjaamon nimi")}
          </Typography>
          <PlaceSearchField value={name} onValueChange={setName} onPlaceSelected={applyPlace} onError={handlePlaceError} placeholder={t("addWorkshop.namePlaceholder", "Syötä korjaamon nimi")} />

          <Typography variant='body2' sx={labelSx}>
            {t("addWorkshop.workshopAddress", "Korjaamon osoite")}
          </Typography>
          <PlaceSearchField value={address} onValueChange={setAddress} onPlaceSelected={applyPlace} onError={handlePlaceError} placeholder={t("addWorkshop.addressPlaceholder", "Syötä korjaamon osoite")} />

          {error && <Alert severity='error'>{error}</Alert>}

          <Box sx={{ position: "relative" }}>
            <GoogleMap
              mapContainerStyle={MAP_STYLE}
              center={initialCenter}
              zoom={14}
              onLoad={(m) => {
                mapRef.current = m;
                setMap(m);
              }}
              onUnmount={() => {
                mapRef.current = null;
                setMap(null);
              }}
              onClick={handleMapClick}
              options={MAP_OPTIONS}
            />
            {loading && <CircularProgress size={28} sx={{ position: "absolute", top: 12, right: 12 }} />}
          </Box>

          <Box>
            {details?.name && (
              <Typography variant='subtitle1' fontWeight={600}>
                {details.name}
              </Typography>
            )}
            <Typography variant='body2'>{address}</Typography>

            {details?.ratings != null && (
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
                <Rating value={details.ratings.rating} precision={0.1} readOnly size='small' />
                <Typography variant='body2'>
                  {details.ratings.rating} ({details.ratings.userRatingCount} {t("addWorkshop.reviews", "Arvostelut")})
                </Typography>
              </Stack>
            )}

            {details?.reviews?.length > 0 && (
              <Stack spacing={1.5} sx={{ mt: 2 }} divider={<Divider flexItem />}>
                {details.reviews.map((r, i) => (
                  <Box key={i}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='subtitle2'>{r.author}</Typography>
                      <Rating value={r.rating} readOnly size='small' />
                      <Typography variant='caption' color='text.secondary'>
                        {r.time}
                      </Typography>
                    </Stack>
                    <Typography variant='body2'>{r.text}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant='contained' disabled={disabled} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" } }} onClick={handleSave}>
            {t("addWorkshop.addWorkshop", "Lisää korjaamo")}
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default AddWorkshop;
